import React, { useState, useEffect } from 'react';
import { 
  Home, Users, Plane, MapPin, Utensils, 
  DollarSign, Clock, Star, X, Save, AlertCircle,
  CheckCircle, Plus, Trash2
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import './PreferencesTab.css';

// Popular airlines for quick selection
const POPULAR_AIRLINES = [
  'Delta', 'United', 'American Airlines', 'Southwest', 'JetBlue',
  'Alaska Airlines', 'Spirit', 'Frontier', 'British Airways',
  'Lufthansa', 'Emirates', 'Air France', 'Qatar Airways'
];

// Popular cities for autocomplete
const POPULAR_CITIES = [
  'New York (JFK)', 'Los Angeles (LAX)', 'Chicago (ORD)', 
  'Miami (MIA)', 'San Francisco (SFO)', 'Boston (BOS)',
  'Seattle (SEA)', 'Dallas (DFW)', 'Atlanta (ATL)',
  'Las Vegas (LAS)', 'London (LHR)', 'Paris (CDG)',
  'Tokyo (NRT)', 'Dubai (DXB)', 'Singapore (SIN)'
];

function PreferencesTab({ user }) {
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [newHomeCity, setNewHomeCity] = useState('');
  const [newDestination, setNewDestination] = useState('');

  // Preferences state
  const [preferences, setPreferences] = useState({
    // Personal Information
    homeCities: [],
    
    // Travel Patterns
    travelsWith: 'alone', // alone, partner, family, friends
    defaultTravelers: 1,
    travelFrequency: 'occasional', // occasional, regular, frequent, veryFrequent
    tripPurpose: 'leisure', // leisure, business, both, family
    
    // Flight Preferences
    favoriteAirlines: [],
    preferredClass: 'economy',
    seatPreference: 'window',
    flightTimePreference: 'flexible',
    
    // Journey Preferences
    layoverTolerance: 'any', // direct, 1stop, any
    maxLayoverDuration: 'any', // 2h, 4h, 8h, any
    favoriteDestinations: [],
    
    // Comfort & Services
    dietaryRestriction: 'none',
    baggagePreference: 'carryon',
    priorityBoarding: false,
    
    // Budget & Deals
    budgetRange: 'flexible',
    flexibleDates: true,
    dealAlerts: true,
    
    // Smart Features
    autoSuggest: true,
    rememberLastSearch: true,
    prefillPassengers: true,
    showJetLagTips: true
  });

  useEffect(() => {
    loadPreferences();
  }, [user]);

  const loadPreferences = async () => {
    try {
      // Load from Supabase user metadata
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      
      if (currentUser?.user_metadata?.preferences) {
        setPreferences({
          ...preferences,
          ...currentUser.user_metadata.preferences
        });
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);
    
    try {
      // Save to Supabase user metadata
      const { error } = await supabase.auth.updateUser({
        data: {
          preferences: preferences
        }
      });
      
      if (error) throw error;
      
      // Also save to localStorage for quick access
      localStorage.setItem(`preferences_${user.id}`, JSON.stringify(preferences));
      
      setSaveMessage({ type: 'success', text: 'Preferences saved successfully!' });
      
      // Clear message after 3 seconds
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('Error saving preferences:', error);
      setSaveMessage({ type: 'error', text: 'Failed to save preferences. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const addHomeCity = () => {
    if (newHomeCity && !preferences.homeCities.includes(newHomeCity)) {
      setPreferences({
        ...preferences,
        homeCities: [...preferences.homeCities, newHomeCity]
      });
      setNewHomeCity('');
    }
  };

  const removeHomeCity = (city) => {
    setPreferences({
      ...preferences,
      homeCities: preferences.homeCities.filter(c => c !== city)
    });
  };

  const addDestination = () => {
    if (newDestination && !preferences.favoriteDestinations.includes(newDestination)) {
      setPreferences({
        ...preferences,
        favoriteDestinations: [...preferences.favoriteDestinations, newDestination]
      });
      setNewDestination('');
    }
  };

  const removeDestination = (dest) => {
    setPreferences({
      ...preferences,
      favoriteDestinations: preferences.favoriteDestinations.filter(d => d !== dest)
    });
  };

  const toggleAirline = (airline) => {
    const current = preferences.favoriteAirlines;
    
    if (current.includes(airline)) {
      setPreferences({
        ...preferences,
        favoriteAirlines: current.filter(a => a !== airline)
      });
    } else {
      setPreferences({
        ...preferences,
        favoriteAirlines: [...current, airline]
      });
    }
  };

  const changeTravelers = (delta) => {
    const newValue = Math.max(1, Math.min(9, preferences.defaultTravelers + delta));
    setPreferences({ ...preferences, defaultTravelers: newValue });
  };

  return (
    <div className="preferences-modern">
      {/* Header */}
      <div className="pref-header">
        <div>
          <h2>Travel Preferences</h2>
          <p>Customize your flight search experience</p>
        </div>
        <button 
          onClick={handleSave}
          disabled={saving}
          className="btn-save"
        >
          <Save className="btn-icon" />
          {saving ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div className={`save-message ${saveMessage.type}`}>
          {saveMessage.type === 'success' ? (
            <CheckCircle className="message-icon" />
          ) : (
            <AlertCircle className="message-icon" />
          )}
          {saveMessage.text}
        </div>
      )}

      {/* Sections */}
      <div className="pref-sections">
        
        {/* Personal Information */}
        <section className="pref-section">
          <div className="section-header">
            <Home className="section-icon" />
            <h3>Personal Information</h3>
          </div>

          <div className="pref-field full-width">
            <label>Home Cities</label>
            <div className="city-input-wrapper">
              <input
                type="text"
                value={newHomeCity}
                onChange={(e) => setNewHomeCity(e.target.value)}
                placeholder="e.g., Dallas (DFW)"
                list="cities-list"
                onKeyPress={(e) => e.key === 'Enter' && addHomeCity()}
                className="input-compact"
              />
              <button onClick={addHomeCity} className="btn-add-small">
                <Plus className="icon-sm" />
                Add
              </button>
            </div>
            <datalist id="cities-list">
              {POPULAR_CITIES.map(city => (
                <option key={city} value={city} />
              ))}
            </datalist>
            <div className="tags-wrapper">
              {preferences.homeCities.map(city => (
                <span key={city} className="tag">
                  {city}
                  <X 
                    className="tag-x" 
                    onClick={() => removeHomeCity(city)}
                  />
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Travel Patterns */}
        <section className="pref-section">
          <div className="section-header">
            <Users className="section-icon" />
            <h3>Travel Patterns</h3>
          </div>

          <div className="pref-grid">
            <div className="pref-field">
              <label>I Usually Travel</label>
              <div className="toggle-group">
                <button 
                  className={`toggle-btn ${preferences.travelsWith === 'alone' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, travelsWith: 'alone'})}
                >
                  Alone
                </button>
                <button 
                  className={`toggle-btn ${preferences.travelsWith === 'partner' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, travelsWith: 'partner'})}
                >
                  Partner
                </button>
                <button 
                  className={`toggle-btn ${preferences.travelsWith === 'family' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, travelsWith: 'family'})}
                >
                  Family
                </button>
                <button 
                  className={`toggle-btn ${preferences.travelsWith === 'friends' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, travelsWith: 'friends'})}
                >
                  Friends
                </button>
              </div>
            </div>

            <div className="pref-field">
              <label>Number of Travelers</label>
              <div className="number-selector">
                <button className="num-btn" onClick={() => changeTravelers(-1)}>−</button>
                <div className="num-display">{preferences.defaultTravelers}</div>
                <button className="num-btn" onClick={() => changeTravelers(1)}>+</button>
              </div>
            </div>

            <div className="pref-field full-width">
              <label>Travel Frequency</label>
              <div className="toggle-group">
                <button 
                  className={`toggle-btn ${preferences.travelFrequency === 'occasional' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, travelFrequency: 'occasional'})}
                >
                  Occasional
                </button>
                <button 
                  className={`toggle-btn ${preferences.travelFrequency === 'regular' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, travelFrequency: 'regular'})}
                >
                  Regular
                </button>
                <button 
                  className={`toggle-btn ${preferences.travelFrequency === 'frequent' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, travelFrequency: 'frequent'})}
                >
                  Frequent
                </button>
                <button 
                  className={`toggle-btn ${preferences.travelFrequency === 'veryFrequent' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, travelFrequency: 'veryFrequent'})}
                >
                  Very Frequent
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Flight Preferences */}
        <section className="pref-section">
          <div className="section-header">
            <Plane className="section-icon" />
            <h3>Flight Preferences</h3>
          </div>

          <div className="pref-grid">
            <div className="pref-field full-width">
              <label>Favorite Airlines</label>
              <div className="chip-group">
                {POPULAR_AIRLINES.map(airline => (
                  <button
                    key={airline}
                    className={`chip ${preferences.favoriteAirlines.includes(airline) ? 'selected' : ''}`}
                    onClick={() => toggleAirline(airline)}
                  >
                    {airline}
                  </button>
                ))}
              </div>
              <small>Select multiple - we'll prioritize these in search results</small>
            </div>

            <div className="pref-field">
              <label>Cabin Class</label>
              <div className="toggle-group">
                <button 
                  className={`toggle-btn ${preferences.preferredClass === 'economy' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, preferredClass: 'economy'})}
                >
                  Economy
                </button>
                <button 
                  className={`toggle-btn ${preferences.preferredClass === 'premium' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, preferredClass: 'premium'})}
                >
                  Premium
                </button>
                <button 
                  className={`toggle-btn ${preferences.preferredClass === 'business' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, preferredClass: 'business'})}
                >
                  Business
                </button>
                <button 
                  className={`toggle-btn ${preferences.preferredClass === 'first' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, preferredClass: 'first'})}
                >
                  First
                </button>
              </div>
            </div>

            <div className="pref-field">
              <label>Seat Preference</label>
              <div className="toggle-group">
                <button 
                  className={`toggle-btn ${preferences.seatPreference === 'window' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, seatPreference: 'window'})}
                >
                  Window
                </button>
                <button 
                  className={`toggle-btn ${preferences.seatPreference === 'aisle' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, seatPreference: 'aisle'})}
                >
                  Aisle
                </button>
                <button 
                  className={`toggle-btn ${preferences.seatPreference === 'middle' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, seatPreference: 'middle'})}
                >
                  Middle
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Journey Preferences */}
        <section className="pref-section">
          <div className="section-header">
            <MapPin className="section-icon" />
            <h3>Journey Preferences</h3>
          </div>

          <div className="pref-grid">
            <div className="pref-field">
              <label>Layover Tolerance</label>
              <div className="toggle-group">
                <button 
                  className={`toggle-btn ${preferences.layoverTolerance === 'direct' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, layoverTolerance: 'direct'})}
                >
                  Direct Only
                </button>
                <button 
                  className={`toggle-btn ${preferences.layoverTolerance === '1stop' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, layoverTolerance: '1stop'})}
                >
                  1 Stop
                </button>
                <button 
                  className={`toggle-btn ${preferences.layoverTolerance === 'any' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, layoverTolerance: 'any'})}
                >
                  Any
                </button>
              </div>
            </div>

            <div className="pref-field">
              <label>Max Layover Duration</label>
              <div className="toggle-group">
                <button 
                  className={`toggle-btn ${preferences.maxLayoverDuration === '2h' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, maxLayoverDuration: '2h'})}
                >
                  2h
                </button>
                <button 
                  className={`toggle-btn ${preferences.maxLayoverDuration === '4h' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, maxLayoverDuration: '4h'})}
                >
                  4h
                </button>
                <button 
                  className={`toggle-btn ${preferences.maxLayoverDuration === '8h' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, maxLayoverDuration: '8h'})}
                >
                  8h
                </button>
                <button 
                  className={`toggle-btn ${preferences.maxLayoverDuration === 'any' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, maxLayoverDuration: 'any'})}
                >
                  Any
                </button>
              </div>
            </div>

            <div className="pref-field full-width">
              <label>Favorite Destinations</label>
              <div className="city-input-wrapper">
                <input
                  type="text"
                  value={newDestination}
                  onChange={(e) => setNewDestination(e.target.value)}
                  placeholder="e.g., London (LHR)"
                  list="cities-list"
                  onKeyPress={(e) => e.key === 'Enter' && addDestination()}
                  className="input-compact"
                />
                <button onClick={addDestination} className="btn-add-small">
                  <Plus className="icon-sm" />
                  Add
                </button>
              </div>
              <div className="tags-wrapper">
                {preferences.favoriteDestinations.map(dest => (
                  <span key={dest} className="tag tag-destination">
                    <Star className="tag-star" />
                    {dest}
                    <X 
                      className="tag-x" 
                      onClick={() => removeDestination(dest)}
                    />
                  </span>
                ))}
              </div>
              <small>Places you visit frequently or love to travel to</small>
            </div>
          </div>
        </section>

        {/* Comfort & Services */}
        <section className="pref-section">
          <div className="section-header">
            <Utensils className="section-icon" />
            <h3>Comfort & Services</h3>
          </div>

          <div className="pref-grid">
            <div className="pref-field">
              <label>Dietary Restrictions</label>
              <div className="toggle-group">
                <button 
                  className={`toggle-btn ${preferences.dietaryRestriction === 'none' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, dietaryRestriction: 'none'})}
                >
                  None
                </button>
                <button 
                  className={`toggle-btn ${preferences.dietaryRestriction === 'vegetarian' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, dietaryRestriction: 'vegetarian'})}
                >
                  Vegetarian
                </button>
                <button 
                  className={`toggle-btn ${preferences.dietaryRestriction === 'vegan' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, dietaryRestriction: 'vegan'})}
                >
                  Vegan
                </button>
                <button 
                  className={`toggle-btn ${preferences.dietaryRestriction === 'halal' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, dietaryRestriction: 'halal'})}
                >
                  Halal
                </button>
              </div>
            </div>

            <div className="pref-field">
              <label>Baggage Preference</label>
              <div className="toggle-group">
                <button 
                  className={`toggle-btn ${preferences.baggagePreference === 'carryon' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, baggagePreference: 'carryon'})}
                >
                  Carry-on
                </button>
                <button 
                  className={`toggle-btn ${preferences.baggagePreference === '1checked' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, baggagePreference: '1checked'})}
                >
                  1 Bag
                </button>
                <button 
                  className={`toggle-btn ${preferences.baggagePreference === '2checked' ? 'active' : ''}`}
                  onClick={() => setPreferences({...preferences, baggagePreference: '2checked'})}
                >
                  2+ Bags
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Smart Features */}
        <section className="pref-section">
          <div className="section-header">
            <Clock className="section-icon" />
            <h3>Smart Features</h3>
          </div>

          <div className="checkbox-cards">
            <label className={`checkbox-card ${preferences.autoSuggest ? 'checked' : ''}`}>
              <input
                type="checkbox"
                checked={preferences.autoSuggest}
                onChange={(e) => setPreferences({...preferences, autoSuggest: e.target.checked})}
              />
              <div className="card-content">
                <div className="card-title">AI Auto-Suggest</div>
                <div className="card-desc">Get smart suggestions based on your travel history</div>
              </div>
            </label>

            <label className={`checkbox-card ${preferences.prefillPassengers ? 'checked' : ''}`}>
              <input
                type="checkbox"
                checked={preferences.prefillPassengers}
                onChange={(e) => setPreferences({...preferences, prefillPassengers: e.target.checked})}
              />
              <div className="card-content">
                <div className="card-title">Pre-fill Details</div>
                <div className="card-desc">Auto-complete passenger information from your profile</div>
              </div>
            </label>

            <label className={`checkbox-card ${preferences.showJetLagTips ? 'checked' : ''}`}>
              <input
                type="checkbox"
                checked={preferences.showJetLagTips}
                onChange={(e) => setPreferences({...preferences, showJetLagTips: e.target.checked})}
              />
              <div className="card-content">
                <div className="card-title">Jet Lag Tips</div>
                <div className="card-desc">Show personalized recommendations to minimize jet lag</div>
              </div>
            </label>
          </div>
        </section>
      </div>

      {/* Footer Save Button */}
      <div className="pref-footer">
        <button 
          onClick={handleSave}
          disabled={saving}
          className="btn-save btn-large"
        >
          <Save className="btn-icon" />
          {saving ? 'Saving Preferences...' : 'Save All Preferences'}
        </button>
      </div>
    </div>
  );
}

export default PreferencesTab;
